/**
 * Notifications API wrapper for the bioapp-v2 proof of concept.
 *
 * All notifications go through the service worker registration rather than the
 * `Notification` constructor. The constructor is unavailable in iOS Safari and
 * throws in Chrome on Android, so the registration path is the only one that
 * works on every target platform.
 */

const SERVICE_WORKER_URL = "/sw.js";

export type PermissionState = NotificationPermission | "unsupported";

export interface EnvironmentReport {
  secureContext: boolean;
  notificationApi: boolean;
  serviceWorkerApi: boolean;
  /** iOS only exposes notifications once the app is launched from the Home Screen. */
  standalone: boolean;
  ios: boolean;
  permission: PermissionState;
}

let registration: ServiceWorkerRegistration | null = null;

function isStandalone(): boolean {
  // iOS predates `display-mode: standalone` and uses a proprietary flag instead.
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

function isIos(): boolean {
  // iPadOS 13+ reports a desktop UA, so also check for a touch-capable "Mac".
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
}

export function describeEnvironment(): EnvironmentReport {
  const notificationApi = "Notification" in window;

  return {
    secureContext: window.isSecureContext,
    notificationApi,
    serviceWorkerApi: "serviceWorker" in navigator,
    standalone: isStandalone(),
    ios: isIos(),
    permission: notificationApi ? Notification.permission : "unsupported",
  };
}

/**
 * Returns the reason notifications cannot work here, or null if they can.
 *
 * These checks exist because every failure mode below is silent in at least one
 * browser: an insecure origin simply hides the API, and iOS Safari resolves the
 * permission request as "denied" without ever prompting outside a Home Screen app.
 */
export function blockingReason(env: EnvironmentReport): string | null {
  if (!env.secureContext) {
    return "This page is not a secure context. Notifications require HTTPS (or localhost).";
  }
  if (!env.serviceWorkerApi) {
    return "This browser has no Service Worker support, which this app requires to show notifications.";
  }
  if (!env.notificationApi) {
    if (env.ios && !env.standalone) {
      return "On iOS, notifications only work after adding this page to the Home Screen (Share → Add to Home Screen) and opening it from there.";
    }
    return "This browser does not implement the Notifications API.";
  }
  if (env.ios && !env.standalone) {
    return "On iOS, open this app from the Home Screen (Share → Add to Home Screen); notifications are blocked in the browser tab.";
  }
  return null;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (registration) return registration;

  registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: "/" });

  // showNotification() needs an *active* worker, which a fresh registration is not.
  await navigator.serviceWorker.ready;

  return registration;
}

/**
 * Requests notification permission.
 *
 * Must be called from a user gesture: Safari and Firefox reject requests that
 * do not originate from a click or tap.
 */
export async function requestPermission(): Promise<PermissionState> {
  if (!("Notification" in window)) return "unsupported";
  return Notification.requestPermission();
}

export async function showNotification(title: string, body: string): Promise<void> {
  const active = await registerServiceWorker();

  await active.showNotification(title, {
    body,
    icon: "/icon-192.png",
    badge: "/icon-192.png",
    // A stable tag means a new notification replaces the previous one instead of
    // stacking up a queue while a device sits on the page for hours.
    tag: "bioapp-v2-heartbeat",
  });
}
